import type { ImageProps } from "next/image";
import blurMap from "./blur-map.json";

const map = blurMap as Record<string, string>;

/**
 * Returns the base64 blur placeholder for a `/...` public path, or
 * `undefined` for SVGs / dynamic URLs / unknown paths.
 *
 * Used directly when the caller knows the src is in the map:
 *   <Image placeholder="blur" blurDataURL={getBlurDataURL(src)} ... />
 *
 * For dynamic / possibly-missing sources, prefer `blurImageProps(src)`
 * below — it returns an empty object when no blur exists, so Next.js
 * doesn't throw "placeholder=blur but no blurDataURL".
 */
export function getBlurDataURL(src: string | undefined | null): string | undefined {
  if (!src) return undefined;
  return map[src];
}

/**
 * Spread-friendly helper: returns `{ placeholder: "blur", blurDataURL }` when
 * the src has a known blur token in the map, otherwise an empty object.
 * Prevents Next.js from throwing when a `placeholder="blur"` is set but
 * the data URL is undefined (happens for SVGs, freshly added images that
 * haven't been re-generated, or future CMS-served URLs).
 *
 *   <Image src={src} {...blurImageProps(src)} ... />
 */
export function blurImageProps(
  src: string | undefined | null,
): Pick<ImageProps, "placeholder" | "blurDataURL"> | Record<string, never> {
  const blurDataURL = getBlurDataURL(src);
  if (!blurDataURL) return {};
  return { placeholder: "blur", blurDataURL };
}
